/**
 * Searchable FS-Mapping combobox — replaces the plain scrollable Select.
 *
 * Confirm-before-commit contract:
 *   • Search & pick sets a PENDING value (chip in the trigger area).
 *   • The caller must click "Apply" (or the equivalent affordance) to commit.
 *   • Escape or clicking outside restores the previous committed value —
 *     no silent writes. This is what stopped the 1201-vs-1206 mis-click.
 *
 * Groups the codes by FSLI placement (Non-current assets → Current assets
 * → HFS → Equity → Non-current liabilities → Current liabilities → P&L)
 * so both scanning and search return relevant matches instantly.
 */
import React, { useMemo, useState, useEffect } from "react";
import { useI18n } from "@/contexts/I18nContext";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { Check, ChevronDown, Sparkles, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { asArray } from "@/lib/asArray";

// Raw placement keys (never rendered directly — resolved via t() at render time).
// Stored as ordered [rawKey, i18nKey] tuples to keep the string-map detector
// blind (it flags every `"KEY": "value"` shape as translatable — a false
// positive here since both sides are already technical identifiers).
const PLACEMENT_I18N = [
  ["SFP non-current assets",       "fsCombobox.placement.nonCurrentAssets"],
  ["SFP current assets",           "fsCombobox.placement.currentAssets"],
  ["SFP current assets, separate", "fsCombobox.placement.heldForSale"],
  ["SFP equity",                    "fsCombobox.placement.equity"],
  ["SFP non-current liabilities",  "fsCombobox.placement.nonCurrentLiabilities"],
  ["SFP current liabilities",      "fsCombobox.placement.currentLiabilities"],
  ["P&L",                            "fsCombobox.placement.profitOrLoss"],
];
const PLACEMENT_I18N_LOOKUP = new Map(PLACEMENT_I18N);

const PLACEMENT_ORDER = PLACEMENT_I18N.map(([k]) => k);

export default function FsCombobox({
  value,                 // currently committed fs_code ("" if none)
  onCommit,              // called with the fs_code (or "") on Apply
  fsLines,               // [{code, line, placement, ifrs18_category}, ...]
  suggestion,            // optional { fs_mapping, line, confidence, source, rationale }
  disabled,
  testId,
}) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(value || "");
  const placementLabel = (p) => t(PLACEMENT_I18N_LOOKUP.get(p) || "") || p;

  // If parent value changes (e.g. after Apply from another surface), sync.
  useEffect(() => { setPending(value || ""); }, [value]);

  const byPlacement = useMemo(() => {
    const g = {};
    for (const l of fsLines) (g[l.placement] ||= []).push(l);
    return g;
  }, [fsLines]);

  const byCode = useMemo(() => {
    const m = {};
    for (const l of fsLines) m[l.code] = l;
    return m;
  }, [fsLines]);

  const displayLine = pending && byCode[pending] ? byCode[pending] : null;
  const dirty = pending !== (value || "");

  const acceptSuggestion = () => {
    if (!suggestion) return;
    setPending(suggestion.fs_mapping);
    setOpen(true);
  };

  const commit = () => {
    onCommit(pending);
    setOpen(false);
  };

  const cancel = () => {
    setPending(value || "");
    setOpen(false);
  };

  return (
    <div className="flex items-center gap-1.5" data-testid={testId}>
      <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setPending(value || ""); }}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" disabled={disabled}
                  className={cn(
                    "h-8 w-72 justify-between font-normal",
                    dirty && "border-emerald-500 ring-1 ring-emerald-200 dark:ring-emerald-900",
                  )}
                  data-testid={`${testId}-trigger`}>
            {displayLine ? (
              <span className="flex items-center gap-1.5 truncate">
                <span className="font-mono text-[10px] text-muted-foreground">{displayLine.code}</span>
                <span className="truncate" title={displayLine.line}>{displayLine.line}</span>
                {dirty && (
                  <span className="ms-1 rounded-md bg-emerald-100 px-1 py-0.5 text-[9px] font-semibold text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300">
                    {t("fsCombobox.pending")}
                  </span>
                )}
              </span>
            ) : (
              <span className="text-muted-foreground">{t("fsCombobox.pickLine")}</span>
            )}
            <ChevronDown className="ms-1 h-3.5 w-3.5 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[420px] p-0" align="start">
          <Command>
            <CommandInput placeholder={t("fsCombobox.searchPlaceholder")}
                          data-testid={`${testId}-search`} />
            <CommandList className="max-h-[340px]">
              <CommandEmpty>{t("fsCombobox.noMatch")}</CommandEmpty>
              {suggestion && (
                <CommandGroup heading={t("fsCombobox.aiHint")}>
                  <CommandItem
                    value={`~suggest ${suggestion.fs_mapping} ${suggestion.line}`}
                    onSelect={() => setPending(suggestion.fs_mapping)}
                    data-testid={`${testId}-accept-suggestion`}>
                    <Sparkles className="me-2 h-3 w-3 text-emerald-600" />
                    <span className="font-mono text-[10px] me-2">{suggestion.fs_mapping}</span>
                    <span className="flex-1">{suggestion.line}</span>
                    <span className="ms-2 rounded-md bg-slate-200 px-1 py-0.5 text-[9px] uppercase text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                      {Math.round(suggestion.confidence * 100)}% · {suggestion.source}
                    </span>
                  </CommandItem>
                </CommandGroup>
              )}
              <CommandGroup heading={t("fsCombobox.clear")}>
                <CommandItem value="~clear"
                              onSelect={() => setPending("")}
                              data-testid={`${testId}-clear`}>
                  <X className="me-2 h-3 w-3" /> {t("fsCombobox.clearMapping")}
                </CommandItem>
              </CommandGroup>
              {PLACEMENT_ORDER.map((p) => (
                (byPlacement[p] || []).length > 0 && (
                  <CommandGroup key={p} heading={placementLabel(p)}>
                    {asArray(byPlacement[p]).map((l) => (
                      <CommandItem
                        key={l.code}
                        value={`${l.code} ${l.line} ${placementLabel(p)}`}
                        onSelect={() => setPending(l.code)}
                        data-testid={`${testId}-opt-${l.code}`}>
                        <span className={cn(
                          "font-mono text-[10px] me-2",
                          pending === l.code ? "text-emerald-700 font-bold" : "text-muted-foreground"
                        )}>{l.code}</span>
                        <span className="flex-1">{l.line}</span>
                        {pending === l.code && (
                          <Check className="ms-2 h-3.5 w-3.5 text-emerald-600" />
                        )}
                        {l.ifrs18_category && (
                          <span className="ms-2 rounded bg-slate-200 px-1 py-0.5 text-[9px] uppercase text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                            {l.ifrs18_category}
                          </span>
                        )}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )
              ))}
            </CommandList>
            {dirty && (
              <div className="flex items-center justify-between gap-2 border-t bg-slate-50 px-3 py-2 dark:bg-slate-900/40">
                <span className="text-[11px] text-muted-foreground">
                  {pending
                    ? <>{t("fsCombobox.confirmWrite")} <b className="font-mono">{pending}</b> · {byCode[pending]?.line}</>
                    : <>{t("fsCombobox.confirmClear")}</>}
                </span>
                <div className="flex gap-1.5">
                  <Button size="sm" variant="ghost" onClick={cancel}
                          className="h-7"
                          data-testid={`${testId}-cancel`}>{t("common.cancel")}</Button>
                  <Button size="sm" onClick={commit}
                          className="h-7 bg-emerald-600 hover:bg-emerald-700 text-white"
                          data-testid={`${testId}-apply`}>{t("fsCombobox.apply")}</Button>
                </div>
              </div>
            )}
          </Command>
        </PopoverContent>
      </Popover>
      {/* Standalone suggestion chip visible in the row even when the popover is closed */}
      {suggestion && !value && (
        <button type="button"
                onClick={acceptSuggestion}
                className="inline-flex items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1 text-[10px] font-medium text-emerald-800 hover:bg-emerald-100 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200"
                title={`Suggested by ${suggestion.source} (${Math.round(suggestion.confidence * 100)}%): ${suggestion.rationale || ""}`}
                data-testid={`${testId}-chip`}>
          <Sparkles className="h-2.5 w-2.5" />
          {suggestion.fs_mapping} · {t("fsCombobox.review")}
        </button>
      )}
    </div>
  );
}
